import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import _ from 'lodash';
import Toolbar from '@material-ui/core/Toolbar';
import Badge from '@material-ui/core/Badge';
import { withStyles } from '@material-ui/core/styles';
import { default as MuiButton } from '@material-ui/core/Button';
import PropTypes from 'prop-types';

import { AppBar, NoticeIcon2, MessageIcon2, FilledIconButton, Tag, Text } from '../../index';

import { GET_UNREAD_NOTIFICATIONS_COUNT, readMessage } from '../../../services/index';
import { GET_STAFF_MESSAGES } from '../../../services/provider';

import socket from '../../../socket';

import Notification from '../Notification/index';
import MessengerWindow from '../../Message/MessengerWindow';

import { decrypt } from '../../../utils/messages';

import { alertError, setMessagesReceiver } from '../../../redux/stateActions';

const messageSound = '/resources/audios/messageSound.mp3';
const notificationSound = '/resources/audios/notificationSound.mp3';

export const StatusButton = ({ onClick, name, value, $tagColor, $color, $fontSize, $bold }) => {
    return (
        <MuiButton onClick={onClick}>
            <div
                style={{
                    display: 'flex',
                    textTransform: 'none',
                    alignItems: 'center',
                    columnGap: '10px',
                }}
            >
                <Text $bold={$bold} $fontSize={$fontSize || '16px'} $color={$color}>
                    {name}
                </Text>
                <Tag $backgroundColor={$tagColor} style={{ padding: '1px 10px' }}>
                    <Text $fontSize="12px" $color="white">
                        {value}
                    </Text>
                </Tag>
            </div>
        </MuiButton>
    );
};

StatusButton.propTypes = {
    onClick: PropTypes.func,
    name: PropTypes.string.isRequired,
    value: PropTypes.number.isRequired,
    $tagColor: PropTypes.string,
    $color: PropTypes.string,
    $fontSize: PropTypes.string,
    $bold: PropTypes.bool,
};

export const ProviderHeader = ({ children }) => {
    const history = useHistory();
    const dispatch = useDispatch();
    const user = useSelector((state) => state.user);
    const messages = useSelector((state) => state.messages);

    const [messageAudio] = useState(new Audio(messageSound));
    const [notificationAudio] = useState(new Audio(notificationSound));

    /*---------- unread notifications count ----------*/
    const [openNoti, setOpenNoti] = useState(false);
    const {
        data: getNotiCountData,
        error: getNotiCountError,
        refetch: getNotiCountRefetch,
    } = useQuery(GET_UNREAD_NOTIFICATIONS_COUNT, {
        variables: { id: user.staff.id },
        skip: _.isEmpty(user),
    });
    useEffect(() => {
        if (getNotiCountError) {
            dispatch(alertError(`Cannot get notification count: ${getNotiCountError.message}`));
        }
    }, [getNotiCountError]);

    const notiCount = useMemo(() => {
        if (getNotiCountData) {
            return getNotiCountData?.notificationsConnection?.aggregate?.count;
        }
        return 0;
    }, [getNotiCountData]);

    const handleOpen = () => {
        setOpenNoti(true);
    };

    window.openNoti = handleOpen;

    const handleClose = () => {
        setOpenNoti(false);
    };

    useEffect(() => {
        if (!openNoti) {
            getNotiCountRefetch();
        }
    }, [openNoti]);

    /*---------- Messenger Window ----------*/
    const messageButtonRef = useRef(null);
    const messageWindowRef = useRef(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [messagesData, setMessagesData] = useState([]);
    const [messagesCount, setMessagesCount] = useState(0);

    const {
        data: getMessagesData,
        error: getMessagesError,
        refetch: getMessageRefetch,
    } = useQuery(GET_STAFF_MESSAGES, {
        variables: { id: user.staff.id },
        skip: _.isEmpty(user),
    });
    useEffect(() => {
        if (getMessagesError) {
            dispatch(alertError(`Cannot get Messages: ${getMessagesError.message}`));
        }
        if (getMessagesData) {
            const decryptMessages = getMessagesData.staff.messages.map((message) =>
                message.text
                    ? {
                          ...message,
                          isRead: ['Provider', 'Provider Admin', 'Admin'].includes(
                              message.senderRole
                          )
                              ? true
                              : message.isRead,
                          text:
                              !message?.staff?.id || !message?.patient?.id
                                  ? 'text unavailable'
                                  : decrypt(
                                        message.text,
                                        ['Provider', 'Provider Admin', 'Admin'].includes(
                                            message.senderRole
                                        )
                                            ? message.staff.id + message.patient.id
                                            : message.patient.id + message.staff.id
                                    ),
                      }
                    : {
                          ...message,
                          isRead: ['Provider', 'Provider Admin', 'Admin'].includes(
                              message.senderRole
                          )
                              ? true
                              : message.isRead,
                      }
            );
            setMessagesCount(decryptMessages.filter((m) => m.isRead === false).length);
            setMessagesData(decryptMessages);
        }
    }, [getMessagesError, getMessagesData]);

    useEffect(() => {
        if (anchorEl) {
            getMessageRefetch();
        }
    }, [anchorEl]);

    const handleMessageClick = (event) => {
        setAnchorEl(anchorEl ? null : event.currentTarget);
    };

    // hide message window if click outsite message button and message window popper
    const handleClickOutside = (event) => {
        if (
            messageButtonRef.current &&
            !messageButtonRef.current.contains(event.target) &&
            messageWindowRef.current &&
            !messageWindowRef.current.contains(event.target)
        ) {
            setAnchorEl(null);
        }
    };
    useEffect(() => {
        document.addEventListener('click', handleClickOutside, false);
        return () => {
            document.removeEventListener('click', handleClickOutside, false);
        };
    }, []);

    const handleClickMessage = async (data) => {
        dispatch(setMessagesReceiver(data));
        setAnchorEl(null);

        if (
            ['Provider', 'Provider Admin', 'Admin'].includes(data.senderRole) ||
            data.isRead === true
        )
            return;

        try {
            const res = await readMessage(data.messageId);

            if (res.status === 200) {
                getMessageRefetch();
            } else {
                throw new Error(res);
            }
        } catch (error) {
            dispatch(alertError(`cannot update message: ${error.message}`));
        }
    };

    useEffect(() => {
        socket.on('newNotification', () => {
            getNotiCountRefetch();
            notificationAudio.volume = 0.5;
            notificationAudio.play();
        });

        socket.off('messageUpdate');

        socket.on('messageUpdate', (data) => {
            getMessageRefetch();
            /* will not play sound if 
            - chat window not open 
            - currently chat with sender
            - in message route
            - this user is sender
            */
            if (messages.id !== data?.sender && data?.sender !== user.staff.id) {
                messageAudio.play();
            }
        });
    }, [messages]);

    const handleSeeAll = () => {
        history.push('/provider/message');
    };

    return (
        <AppBar style={{ maxWidth: 'none' }}>
            <MessengerWindow
                showTitle
                myRole="provider"
                anchorEl={anchorEl}
                ref={messageWindowRef}
                data={messagesData}
                onClick={handleClickMessage}
                onSeeAll={handleSeeAll}
            />
            <Notification open={openNoti} handleClose={handleClose} />
            <Toolbar disableGutters>
                <div
                    style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        // justifyContent: 'space-between',
                    }}
                >
                    {children}
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            flex: '1 1 0',
                        }}
                    >
                        <div ref={messageButtonRef}>
                            <FilledIconButton
                                $backgroundColor="gray1"
                                $width="40px"
                                $height="40px"
                                onClick={handleMessageClick}
                            >
                                <StyledBadge
                                    color="secondary"
                                    badgeContent={messagesCount}
                                    overlap="rectangular"
                                >
                                    <MessageIcon2 />
                                </StyledBadge>
                            </FilledIconButton>
                        </div>

                        <FilledIconButton
                            $backgroundColor="gray1"
                            $width="40px"
                            $height="40px"
                            onClick={handleOpen}
                        >
                            <StyledBadge
                                badgeContent={notiCount}
                                color="secondary"
                                overlap="rectangular"
                            >
                                <NoticeIcon2 />
                            </StyledBadge>
                        </FilledIconButton>
                    </div>
                </div>
            </Toolbar>
        </AppBar>
    );
};

ProviderHeader.propTypes = {
    children: PropTypes.object,
};

const StyledBadge = withStyles((theme) => ({
    badge: {
        color: 'white',
        marginRight: '-5px',
        backgroundColor: theme.palette.common.orange,
    },
}))(Badge);
